/**
 * TemplateConnectionsContent
 *
 * Embedded content for configuring template connections, rendered inside
 * the Connections tab of TemplateConfigDialog.
 *
 * Uses the same data source (live API + local store), filtering logic,
 * display components, and creation flow as ConnectionTab.
 */

import { useState, useMemo, useCallback, useEffect, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Badge,
  type AccordionToggleEventHandler,
} from '@coreai/fluentui-react';
import {
  PlugDisconnected16Regular,
  Search16Regular,
  Add16Regular,
} from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useConnectionStore } from '@/stores/connectionStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { isConnectionReferenceComplete } from '@/services/connectionService';
import { useConnections, connectionKeys, ensureConnectorMetadata, useAppContextBuilder } from '@/hooks/useConnectionMutations';
import { useGetConnectorInfo } from '@/contexts';
import type { ConnectionParameterSet } from '@/types/connectionParameters';
import { isManagedIdentityParameterSet } from '@/types/connectionParameters';
import { extractConnectorName, isAgentConnectionId, isManagedApiConnector, normalizeApiId, toShortManagedApiId, getValidConnections, referenceHasConnection, mapApiConnectionsToMerged, classifyMergedConnectionType, filterConnectionsBySearch, type MergedConnection } from '@/utils/connections';
import { remapWorkflowReference as applyReferenceRemap } from '@/utils/templates';
import { CompactConnectionItem } from '@/components/common/CompactConnectionItem';
import { ConnectionCreationPanel } from '@/components/WorkflowDesigner/NodeEditor/dialogs/ConnectionCreationPanel';
import { thinScrollbarStyles } from '@/styles/scrollbar';
import type { TemplateConnectionRequirement } from '@/types/template';
import type { ConnectionReference } from '@/types/connectionReference';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: tokens.spacingHorizontalXS,
    ...thinScrollbarStyles,
  },
  creationHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  creationTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  connectionItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
  },
  connectionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  connectionName: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  connectionDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  footerText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  connectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  createNewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  createNewIcon: {
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  createNewText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    gap: tokens.spacingVerticalS,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeBase600,
    color: tokens.colorNeutralForeground4,
  },
  emptyText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  emptyInline: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginLeft: tokens.spacingHorizontalXL,
  },
  searchInput: {
    width: '100%',
    marginBottom: tokens.spacingVerticalS,
  },
});

// =============================================================================
// TYPES
// =============================================================================

export interface TemplateConnectionsContentProps {
  connectionRequirements: TemplateConnectionRequirement[];
  onCreateConnectionModeChange?: (isCreating: boolean) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/** Format connector name for display when no connector info is available */
function formatConnectorDisplayName(name: string): string {
  if (!name) return 'Unknown';
  // Convert camelCase to Title Case with spaces
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
}

/**
 * Remap a single workflow reference (`oldRefKey` -> `newRefKey`) by wiring the
 * pure `@/utils/templates` `remapWorkflowReference` helper to the real workflow
 * and connection stores.
 *
 * The barrel helper stays store-free; this adapter supplies the store surface
 * (current workflow, reference existence check, node rebind, workflow write).
 * Exported so the store wiring can be exercised end-to-end against the real
 * stores in tests, independent of rendering the dialog.
 */
export function remapWorkflowReferenceInStores(oldRefKey: string, newRefKey: string): void {
  applyReferenceRemap(oldRefKey, newRefKey, {
    workflow: useWorkflowStore.getState().workflow,
    hasReference: (key) => !!useConnectionStore.getState().references[key],
    remapNodesToReference: (oldKey, newKey) =>
      useConnectionStore.getState().remapNodesToReference(oldKey, newKey),
    updateWorkflow: (workflow) => useWorkflowStore.getState().updateWorkflow(workflow),
  });
}

export const TemplateConnectionsContent = ({
  connectionRequirements,
  onCreateConnectionModeChange,
}: TemplateConnectionsContentProps) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  // Tracks which row's "Create new" button is active. Carries the row's apiId
  // (for the creation panel) and selectionKey (so the completion handler writes
  // back into the same row's selection slot).
  const [creatingFor, setCreatingFor] = useState<
    { apiId: string; selectionKey: string; referenceKey?: string } | null
  >(null);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [openItems, setOpenItems] = useState<string[]>([]);

  // Stores
  const connectionReferences = useConnectionStore((s) => s.references);
  const addConnectionReference = useConnectionStore((s) => s.addConnectionReference);

  // Current selection per connector (selectionKey → referenceKey). Seeded from
  // the workflow's existing complete connection references and updated locally
  // as the user picks connections. (No template-mode store — this works for any
  // workflow.)
  const [connectionSelections, setConnectionSelectionState] = useState<Record<string, string>>({});
  const setConnectionSelection = useCallback((selectionKey: string, referenceKey: string) => {
    setConnectionSelectionState((prev) => ({ ...prev, [selectionKey]: referenceKey }));
  }, []);

  // Seed selections from the live references. Each row is bound to a specific
  // `referenceKey`, so the seed is just that reference when it's complete; no
  // apiId-wide fallback. User picks (held in local state) take precedence over
  // the derived seed.
  useEffect(() => {
    const seeded: Record<string, string> = {};
    for (const req of connectionRequirements) {
      if (!req.referenceKey) continue;
      const ref = connectionReferences[req.referenceKey];
      if (ref && isConnectionReferenceComplete(ref)) {
        seeded[req.referenceKey] = req.referenceKey;
      }
    }
    setConnectionSelectionState((prev) => ({ ...seeded, ...prev }));
  }, [connectionRequirements, connectionReferences]);

  // Fetch live connections from data-plane API (same as ConnectionTab)
  const { data: apiConnections, isLoading: isLoadingConnections } = useConnections();
  const validApiConnections = useMemo(() => getValidConnections(apiConnections ?? []), [apiConnections]);
  const { buildContext } = useAppContextBuilder();

  // Resolve connector icons
  const getConnectorInfo = useGetConnectorInfo();

  useEffect(() => {
    onCreateConnectionModeChange?.(creatingFor !== null);
  }, [creatingFor, onCreateConnectionModeChange]);

  // Helper to get display name for a requirement
  const getDisplayName = useCallback(
    (apiId: string) => {
      const connectorKey = extractConnectorName(apiId);
      const info = getConnectorInfo(connectorKey);
      return info?.displayName ?? formatConnectorDisplayName(connectorKey);
    },
    [getConnectorInfo]
  );

  // Per-requirement selection key: the `referenceKey` (each row owns its own
  // slot so two rows sharing an apiId don't collide). Falls back to the
  // normalized apiId for degenerate requirements that arrive without a
  // referenceKey.
  const getReqSelectionKey = useCallback(
    (req: TemplateConnectionRequirement) =>
      req.referenceKey ?? normalizeApiId(req.apiId),
    []
  );

  const handleAccordionToggle = useCallback<AccordionToggleEventHandler<string>>(
    (_event, data) => {
      setOpenItems(data.openItems);
    },
    []
  );

  // Activate the given action on Enter or Space, suppressing default scroll
  // for Space. Shared by both `role="button"` Create-new controls below.
  const handleActivateKey = useCallback(
    (event: ReactKeyboardEvent, action: () => void) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        action();
      }
    },
    []
  );

  // Build merged connection list: API connections + local store references (deduplicated)
  const allConnections = useMemo((): MergedConnection[] => {
    // 1. Map API connections
    const connections: MergedConnection[] = mapApiConnectionsToMerged(
      validApiConnections,
      getConnectorInfo,
    );

    // 2. Merge locally-created connections from the store that aren't in the API response
    const apiKeys = new Set(connections.map((c) => c.key));
    for (const [refKey, ref] of Object.entries(connectionReferences)) {
      if (apiKeys.has(refKey)) continue;
      // Skip template-apply placeholders. `extractConnectionReferences` mints
      // `connection.id = refKey` for SP/agent entries, so `referenceHasConnection`
      // (which returns true for any non-managed-API ref) would otherwise let
      // the placeholder appear in this dialog's connector picker as if it were
      // a usable existing connection.
      if (!isConnectionReferenceComplete(ref)) continue;
      const apiId = ref.api?.id ?? '';
      const connType = classifyMergedConnectionType(apiId);

      if (referenceHasConnection(ref, validApiConnections)) {
        const apiName = extractConnectorName(apiId);
        const info = getConnectorInfo(refKey) ?? getConnectorInfo(apiName);
        connections.push({
          key: refKey,
          name: ref.connectionName ?? refKey,
          type: connType,
          apiId,
          ...(info?.iconUri ? { iconUri: info.iconUri } : {}),
        });
      }
    }

    return connections;
  }, [validApiConnections, connectionReferences, getConnectorInfo]);

  // Filter connections for a specific requirement using normalized apiId matching.
  // This ensures managed API and service provider connections are matched correctly.
  const getConnectionsForRequirement = useCallback(
    (requirementApiId: string, searchQuery?: string) => {
      const normalizedReq = normalizeApiId(requirementApiId);
      let filtered = allConnections.filter((conn) => {
        // Match connections by normalized apiId to ensure type compatibility
        return normalizeApiId(conn.apiId) === normalizedReq;
      });

      // Apply search query if provided
      if (searchQuery) {
        filtered = filterConnectionsBySearch(filtered, searchQuery);
      }

      return filtered;
    },
    [allConnections]
  );

  // Count configured rows. Uses the per-requirement selection key so rows in
  // by-reference mode (which share an apiId but have distinct refKeys) are
  // counted independently.
  const configuredCount = useMemo(() => {
    return connectionRequirements.filter(
      (req) => connectionSelections[getReqSelectionKey(req)]
    ).length;
  }, [connectionRequirements, connectionSelections, getReqSelectionKey]);

  // Remap a single workflow reference (old → new) after the user picks a
  // different connection. Delegates to the module-level store adapter (which
  // wires the pure `@/utils/templates` helper to the workflow + connection
  // stores); wrapped in useCallback so the dependent handlers below stay stable.
  const remapWorkflowReference = useCallback(remapWorkflowReferenceInStores, []);

  // Selection handler. Each row owns one workflow reference; picking a
  // different reference rebinds *only* the affected nodes (other workflow
  // nodes sharing the same connector are untouched, and no reference is
  // deleted from connections.json).
  //
  // The list is a merge of (a) existing references in connections.json and
  // (b) API connections that aren't yet in connections.json. For case (b),
  // we mint a new reference into the store *before* remapping so the
  // workflow has a valid reference to bind to.
  //
  // `sourceRefKey` is the row's *current* binding (initially the original
  // requirement key, then the previously-picked key on subsequent picks).
  // Passing the current binding is what keeps `remapWorkflowReference`
  // chainable without the dialog ever needing to re-derive its rows.
  const handleSelectConnection = useCallback(
    (selectionKey: string, sourceRefKey?: string) => async (referenceKey: string) => {
      setConnectionSelection(selectionKey, referenceKey);

      const apiConn =
        !connectionReferences[referenceKey] && apiConnections
          ? apiConnections.find((c) => c.name === referenceKey)
          : undefined;
      if (apiConn) {
        const apiId = apiConn.connectorId ?? '';
        // MI status comes from the connector manifest (loaded on demand);
        // audience is resolved lazily at invoke time via useMiAudience.
        const metadata = apiId ? await ensureConnectorMetadata(queryClient, buildContext, apiId) : null;
        const isManagedIdentityAuth = isManagedIdentityParameterSet(
          apiConn.properties?.parameterValueSet?.name,
          metadata?.connectionParameterSets as readonly ConnectionParameterSet[] | undefined
        );
        const ref: ConnectionReference = {
          api: { id: isManagedApiConnector(apiId) ? toShortManagedApiId(apiId) : apiId },
          connection: { id: apiConn.id },
          connectionName: apiConn.displayName ?? apiConn.name,
          ...(isManagedIdentityAuth
            ? { authentication: { type: 'ManagedServiceIdentity' as const } }
            : {}),
        };
        addConnectionReference(referenceKey, ref);
      }

      if (sourceRefKey && sourceRefKey !== referenceKey) {
        remapWorkflowReference(sourceRefKey, referenceKey);
      }
    },
    [setConnectionSelection, connectionReferences, apiConnections, addConnectionReference, queryClient, buildContext, remapWorkflowReference]
  );

  // Creation complete handler.
  //
  // When the row being satisfied was originally a template-apply *placeholder*
  // (an entry with only api/modelProvider + displayName and no real connection
  // config), replace that placeholder entirely instead of leaving it beside the
  // new connection in `connections.json`.
  //
  // `replaceConnectionKey` atomically deletes the placeholder from `references`
  // AND from the relevant `originalConnectionsConfig` buckets (managedApi/serviceProvider/agent/agentMcp), then inserts the new
  // reference. It also remaps node bindings, but the subsequent
  // `remapWorkflowReference` is still needed to patch the workflow definition
  // itself. When the new refKey equals the placeholder key (user kept the same
  // name), passing `sourceRefKey` still clears the placeholder shell so the
  // export rebuilds the entry from the fresh reference.
  //
  // Real, already-configured connections are left alone: the "Create new"
  // action there should append a brand-new connection without deleting the
  // existing one (it may be used by other workflows in the same app).
  const handleCreateComplete = useCallback(
    (selectionKey: string, sourceRefKey?: string) => (refKey: string, ref: ConnectionReference) => {
      const existingSourceRef = sourceRefKey
        ? useConnectionStore.getState().references[sourceRefKey]
        : undefined;
      const sourceIsPlaceholder =
        !!sourceRefKey &&
        !!existingSourceRef &&
        !isConnectionReferenceComplete(existingSourceRef);

      if (sourceIsPlaceholder) {
        useConnectionStore.getState().replaceConnectionKey(sourceRefKey, refKey, ref);
      } else {
        addConnectionReference(refKey, ref);
      }

      setConnectionSelection(selectionKey, refKey);
      if (sourceRefKey && sourceRefKey !== refKey) {
        remapWorkflowReference(sourceRefKey, refKey);
      }
      setCreatingFor(null);
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
    },
    [addConnectionReference, setConnectionSelection, queryClient, remapWorkflowReference]
  );

  // Render creation panel when active
  if (creatingFor !== null) {
    const { apiId: creatingForApiId, selectionKey, referenceKey: creatingForRefKey } = creatingFor;
    // Use the apiId directly if it starts with '/', otherwise prefix it
    const connectorId = creatingForApiId.startsWith('/') ? creatingForApiId : `/${creatingForApiId}`;
    const displayName = getDisplayName(creatingForApiId);
    // Agent connections render through a specialized AgentConnectionForm
    // inside ConnectionCreationPanel (subscription + cognitive-service-account
    // pickers, etc.). That branch is gated on connectorType === 'Agent' AND
    // a non-empty agentModelType — without both, the panel falls back to the
    // generic DynamicConnectionForm and the UX diverges from the per-node
    // Connections tab. Requirements that still need a BYOM agent connection
    // (for example, when Microsoft-managed is unavailable) don't carry a
    // model type, so use AzureOpenAI for this connection-creation path.
    const isAgent = isAgentConnectionId(connectorId);

    return (
      <div className={styles.root}>
        <div className={styles.body}>
          <ConnectionCreationPanel
            title={`Creating new connection for ${displayName}`}
            {...(connectorId ? { connectorId } : {})}
            connectorName={displayName}
            {...(isAgent ? { connectorType: 'Agent', agentModelType: 'AzureOpenAI' as const } : {})}
            onComplete={handleCreateComplete(selectionKey, creatingForRefKey)}
            onBack={() => setCreatingFor(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Body */}
      <div className={styles.body}>
        {connectionRequirements.length === 0 ? (
          <div className={styles.emptyState}>
            <PlugDisconnected16Regular className={styles.emptyIcon} />
            <Text className={styles.emptyText}>This template has no connections to configure.</Text>
          </div>
        ) : (
          <Accordion multiple collapsible openItems={openItems} onToggle={handleAccordionToggle}>
            {connectionRequirements.map((req) => {
              const selectionKey = getReqSelectionKey(req);
              const displayName = getDisplayName(req.apiId);
              const selected = connectionSelections[selectionKey];
              const isConfigured = !!selected;
              const searchQuery = searchQueries[selectionKey] ?? '';
              const matchingConnections = getConnectionsForRequirement(req.apiId, searchQuery);
              const showSearch = !isLoadingConnections && getConnectionsForRequirement(req.apiId).length > 5;
              // The row's identity (`selectionKey`) is frozen for the dialog
              // session so the AccordionItem never remounts. The *current*
              // workflow binding lives in `selected` and is what subsequent
              // remaps must source from — if the user picks A then C, the
              // second pick needs to remap A→C if no edit happened, or B→C if
              // they previously picked B. Fall back to the original frozen
              // refKey when the row hasn't been touched yet.
              const currentBinding = selected ?? req.referenceKey;
              const openCreate = () =>
                setCreatingFor({
                  apiId: req.apiId,
                  selectionKey,
                  ...(currentBinding ? { referenceKey: currentBinding } : {}),
                });

              return (
                <AccordionItem key={selectionKey} value={selectionKey}>
                  <AccordionHeader>
                    <div className={styles.connectionLabel}>
                      <Text className={styles.connectionName}>{displayName}</Text>
                      {isConfigured ? (
                        <Badge appearance="tint" color="success" size="medium">
                          Connected
                        </Badge>
                      ) : (
                        <Badge appearance="tint" color="warning" size="medium">
                          Not connected
                        </Badge>
                      )}
                    </div>
                  </AccordionHeader>
                  <AccordionPanel>
                    <div className={styles.connectionItem}>

                      {/* Search input (shown only when > 5 connections) */}
                      {showSearch && (
                        <Input
                          className={styles.searchInput}
                          contentBefore={<Search16Regular />}
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={(_, data) =>
                            setSearchQueries((prev) => ({
                              ...prev,
                              [selectionKey]: data.value,
                            }))
                          }
                          appearance="filled-darker"
                          size="small"
                        />
                      )}

                      {/* Connection list */}
                      {isLoadingConnections ? (
                        <div className={styles.loadingContainer}>
                          <Spinner size="tiny" />
                        </div>
                      ) : matchingConnections.length === 0 ? (
                        <div className={styles.emptyInline}>
                          <Text className={styles.emptyText}>No connections found.</Text>
                          <div
                            className={styles.createNewButton}
                            onClick={openCreate}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => handleActivateKey(e, openCreate)}
                            aria-label={`Create new connection for ${displayName}`}
                          >
                            <div className={styles.createNewIcon}>
                              <Add16Regular />
                            </div>
                            <Text className={styles.createNewText}>Create new</Text>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            className={styles.connectionList}
                            role="listbox"
                            aria-label={`Available connections for ${displayName}`}
                          >
                            {matchingConnections.map((conn) => (
                              <CompactConnectionItem
                                key={conn.key}
                                name={conn.name}
                                referenceKey={conn.key}
                                showReferenceKey
                                {...(conn.iconUri ? { iconUri: conn.iconUri } : {})}
                                isAI={conn.type === 'agent'}
                                status="connected"
                                isSelected={selected === conn.key}
                                onSelect={handleSelectConnection(selectionKey, currentBinding)}
                              />
                            ))}
                          </div>

                          {/* Create new connection — only show when there are existing connections */}
                          <div
                            className={styles.createNewButton}
                            onClick={openCreate}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => handleActivateKey(e, openCreate)}
                            aria-label={`Create new connection for ${displayName}`}
                          >
                            <div className={styles.createNewIcon}>
                              <Add16Regular />
                            </div>
                            <Text className={styles.createNewText}>Create new</Text>
                          </div>
                        </>
                      )}
                    </div>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>

      {/* Footer */}
      {connectionRequirements.length > 0 && (
        <div className={styles.footer}>
          <Text className={styles.footerText}>
            {configuredCount} of {connectionRequirements.length} connections configured
          </Text>
        </div>
      )}
    </div>
  );
};

TemplateConnectionsContent.displayName = 'TemplateConnectionsContent';
